import { axiosInstance } from "@/lib/axios";
import queryClient from "@/lib/queryClient";
import { categorySchema, CategoryType, categoryUpdateSchema, CategoryUpdateType } from "@/schemas/category-schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

export const CategoryQuerySchema = z.object({
  limit: z.number().min(1).max(100).optional(),
  offset: z.number().min(0).optional(),
});

export type CategoryCreateType = z.infer<typeof categorySchema>;
export type CategoryQueryType = z.infer<typeof CategoryQuerySchema>;

// API functions
export async function getCategoriesFn(query: CategoryQueryType = {}) {
  const params = new URLSearchParams();
  
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.offset) params.append('offset', query.offset.toString());
  
  const url = `/api/categories${params.toString() ? `?${params.toString()}` : ''}`;
  return (await axiosInstance.get<{data: CategoryType[], pagination: {total: number, limit: number, offset: number, hasMore: boolean}}>(url)).data;
}

export async function getCategoryByIdFn(id: number) {
  return (await axiosInstance.get<CategoryType>(`/api/categories/${id}`)).data;
}

export async function createCategoryFn(data: CategoryCreateType) {
  return (await axiosInstance.post<CategoryType>("/api/categories", data)).data;
}

export async function updateCategoryFn(data: CategoryUpdateType) {
  const { id, ...updateData } = data;
  return (await axiosInstance.put(`/api/categories/${id}`, updateData)).data;
}

export async function deleteCategoryFn(id: number) {
  return (await axiosInstance.delete(`/api/categories/${id}`)).data;
}

const Category = {
  Get: {
    useQuery: (query: CategoryQueryType = {}, options = {}) =>
      useQuery({
        queryKey: ["Categories", query],
        queryFn: () => getCategoriesFn(query),
        ...options,
      }),
  },
  
  GetById: {
    useQuery: (id: number, options = {}) =>
      useQuery({
        queryKey: ["Category", id],
        queryFn: () => getCategoryByIdFn(id),
        enabled: !!id,
        ...options,
      }),
  },

  Create: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: createCategoryFn,
        onSuccess: (data) => {
          toast.success(`Category "${data.name}" created successfully`);
          queryClient.invalidateQueries({ queryKey: ["Categories"] });
        },
        onError: (error: any) => {
          const message = error?.response?.data?.error || "Failed to create category";
          toast.error(message);
        },
        ...options,
      }),
  },

  Update: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: updateCategoryFn,
        onSuccess: (data) => {
          toast.success(`Category "${data.name}" updated successfully`);
          queryClient.invalidateQueries({ queryKey: ["Categories"] });
          queryClient.invalidateQueries({ queryKey: ["Category", data.id] });
        },
        onError: (error: any) => {
          const message = error?.response?.data?.error || "Failed to update category";
          toast.error(message);
        },
        ...options,
      }),
  },

  Delete: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: deleteCategoryFn,
        onSuccess: () => {
          toast.success("Category deleted successfully");
          queryClient.invalidateQueries({ queryKey: ["Categories"] });
        },
        onError: (error: any) => {
          const message = error?.response?.data?.error || "Failed to delete category";
          toast.error(message);
        },
        ...options,
      }),
  },
};

export default Category;
